import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

type ComboboxOption = { value: string; label: string };

interface FilterComboboxProps {
  value: string;
  onChange: (value: string) => void;
  /**
   * Either bare strings (value === label) or explicit {value, label} pairs.
   * Do NOT include the "no filter" default here — it's rendered from `placeholder`/`emptyValue`.
   */
  options: readonly (string | ComboboxOption)[];
  /** Label for the default row and the trigger when `emptyValue` is selected. */
  placeholder?: string;
  /** The value that represents "no filter"/default. Defaults to '' (empty string). */
  emptyValue?: string;
  ariaLabel?: string;
}

/**
 * Searchable single-select for a filter field. Options may be values found in the
 * data (bare strings) or fixed {value, label} pairs. Selecting the default row
 * emits `emptyValue` (empty string = "Any" by default), which means "no filter".
 */
export function FilterCombobox({
  value,
  onChange,
  options,
  placeholder = 'Any',
  emptyValue = '',
  ariaLabel,
}: FilterComboboxProps) {
  const [open, setOpen] = useState(false);

  const normalized: ComboboxOption[] = options.map((o) =>
    typeof o === 'string' ? { value: o, label: o } : o
  );

  const isEmpty = value === emptyValue;
  const selectedLabel = isEmpty
    ? placeholder
    : normalized.find((o) => o.value === value)?.label ?? value;

  const select = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          className="h-8 w-full justify-between text-sm font-normal"
        >
          <span className={cn('truncate', isEmpty && 'text-muted-foreground')}>
            {selectedLabel}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search..." className="h-9" />
          <CommandList>
            <CommandEmpty>No matches found.</CommandEmpty>
            <CommandGroup>
              <CommandItem value={placeholder} onSelect={() => select(emptyValue)}>
                <Check className={cn('mr-2 h-4 w-4', isEmpty ? 'opacity-100' : 'opacity-0')} />
                <span className="text-muted-foreground">{placeholder}</span>
              </CommandItem>
              {normalized.map((opt) => (
                <CommandItem key={opt.value} value={opt.label} onSelect={() => select(opt.value)}>
                  <Check className={cn('mr-2 h-4 w-4', value === opt.value ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">{opt.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
